import React, { useState, useCallback, useRef, memo } from "react";
import {
  UPLOAD_IMAGE_MAX_COUNT,
  UPLOAD_IMAGE_MAX_SIZE_BYTES,
  UPLOAD_IMAGE_ACCEPTED_TYPES,
} from "../services/aiImageService";
import SettingTooltip from "./SettingTooltip";

/**
 * UserImageUploader - Component quản lý ảnh người dùng (preview-only mode)
 * Hỗ trợ: chọn file (preview local), drag-and-drop, reorder bằng kéo thả, xóa ảnh.
 * KHÔNG upload lên Cloudinary — việc upload được thực hiện khi nhấn "Lưu thiết lập"
 * trong AutoPostGenerator (defer upload để tránh lưu ảnh thừa trên Cloudinary).
 */
/**
 * Tính trạng thái đăng bài cho mỗi ảnh dựa trên cursor.
 * cursor = tổng số lần cron đã chọn ảnh (tăng 1 mỗi lần đăng thành công).
 * Ảnh được chọn theo: imageIndex = cursor % images.length (wrap-around).
 */
function getImagePostingStatus(index, cursor, totalImages) {
  if (totalImages === 0) return null;
  const currentPos = cursor % totalImages; // Vị trí ảnh tiếp theo sẽ được dùng
  const fullCycles = Math.floor(cursor / totalImages); // Số vòng đã hoàn thành

  if (index < currentPos) {
    // Ảnh trước cursor trong vòng hiện tại → đã dùng (fullCycles + 1) lần
    return { status: "posted", usedCount: fullCycles + 1 };
  } else if (index === currentPos) {
    // Ảnh tại cursor → sẽ được dùng tiếp theo
    return { status: "next", usedCount: fullCycles };
  } else {
    // Ảnh sau cursor → đã dùng fullCycles lần
    return { status: "pending", usedCount: fullCycles };
  }
}

/**
 * Bọc bằng React.memo để tránh re-render khi parent re-render
 * nhưng props không thực sự thay đổi về giá trị.
 */
const UserImageUploader = memo(({
  images = [],
  onImagesChange,
  maxImages = UPLOAD_IMAGE_MAX_COUNT,
  maxSizeBytes = UPLOAD_IMAGE_MAX_SIZE_BYTES,
  disabled = false,
  uploadImageCursor = 0,
  isEditMode = false,
}) => {
  // State quản lý drag-drop file upload
  const [isDragOverZone, setIsDragOverZone] = useState(false);
  // State quản lý drag-drop reorder
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  // State lỗi chung
  const [errorMessage, setErrorMessage] = useState("");

  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // ===== VALIDATION =====
  const validateFile = useCallback(
    (file) => {
      if (!UPLOAD_IMAGE_ACCEPTED_TYPES.includes(file.type)) {
        return `"${file.name}" không đúng định dạng. Chỉ chấp nhận JPG, PNG.`;
      }
      if (file.size > maxSizeBytes) {
        const maxMB = (maxSizeBytes / (1024 * 1024)).toFixed(0);
        return `"${file.name}" vượt quá ${maxMB}MB. Vui lòng chọn ảnh nhỏ hơn.`;
      }
      return null;
    },
    [maxSizeBytes]
  );

  // ===== HANDLE FILES SELECTED/DROPPED (Preview-only, no upload) =====
  const handleFiles = useCallback(
    (fileList) => {
      if (disabled) return;

      setErrorMessage("");
      const files = Array.from(fileList);
      const remainingSlots = maxImages - images.length;

      if (remainingSlots <= 0) {
        setErrorMessage(`Đã đạt giới hạn ${maxImages} ảnh.`);
        return;
      }

      if (files.length > remainingSlots) {
        setErrorMessage(
          `Chỉ có thể thêm ${remainingSlots} ảnh nữa. Đã bỏ qua ${files.length - remainingSlots} ảnh.`
        );
      }

      const filesToProcess = files.slice(0, remainingSlots);
      const validationErrors = [];
      const validFiles = [];

      // Validate tất cả file trước
      for (const file of filesToProcess) {
        const error = validateFile(file);
        if (error) {
          validationErrors.push(error);
        } else {
          validFiles.push(file);
        }
      }

      if (validationErrors.length > 0) {
        setErrorMessage(validationErrors.join(" "));
      }

      if (validFiles.length === 0) return;

      // Tạo local previews — không upload Cloudinary
      const newImages = validFiles.map((file, idx) => {
        const previewUrl = URL.createObjectURL(file);
        return {
          url: previewUrl,
          filename: file.name,
          order: images.length + idx + 1,
          _file: file,       // Giữ File object để upload khi Lưu thiết lập
          _isLocal: true,    // Đánh dấu ảnh chưa upload Cloudinary
        };
      });

      const updatedImages = [...images, ...newImages];
      onImagesChange(updatedImages);
    },
    [disabled, maxImages, images, validateFile, onImagesChange]
  );

  // ===== FILE INPUT CHANGE =====
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      // Reset input để có thể chọn lại cùng file
      e.target.value = "";
    }
  };

  // ===== DROP ZONE EVENTS (File upload) =====
  const handleDragEnterZone = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    // Chỉ kích hoạt khi drag file từ bên ngoài (không phải reorder)
    if (draggedIndex === null && e.dataTransfer.types.includes("Files")) {
      setIsDragOverZone(true);
    }
  };

  const handleDragLeaveZone = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOverZone(false);
    }
  };

  const handleDragOverZone = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDropZone = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverZone(false);
    dragCounter.current = 0;

    // Chỉ xử lý khi drop file từ ngoài, không phải reorder
    if (draggedIndex === null && e.dataTransfer.files?.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // ===== DRAG & DROP REORDER EVENTS =====
  const handleDragStart = (e, index) => {
    if (disabled) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Cần set data để drag hoạt động trên một số browser
    e.dataTransfer.setData("text/plain", index.toString());
    // Thêm hiệu ứng opacity sau 1 frame để ghost image hiển thị đúng
    requestAnimationFrame(() => {
      e.target.style.opacity = "0.4";
    });
  };

  const handleDragEnterItem = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragOverItem = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropItem = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder mảng images
    const reordered = [...images];
    const [movedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, movedItem);

    // Cập nhật lại order
    const updatedImages = reordered.map((img, idx) => ({
      ...img,
      order: idx + 1,
    }));

    onImagesChange(updatedImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // ===== DELETE IMAGE =====
  const handleDeleteImage = (index) => {
    if (disabled) return;
    const imageToDelete = images[index];
    // Revoke blob URL nếu là ảnh local để tránh memory leak
    if (imageToDelete?._isLocal && imageToDelete?.url) {
      URL.revokeObjectURL(imageToDelete.url);
    }
    const updated = images.filter((_, i) => i !== index);
    // Cập nhật lại order
    const reordered = updated.map((img, idx) => ({
      ...img,
      order: idx + 1,
    }));
    onImagesChange(reordered);
  };

  // ===== FORMAT FILE SIZE =====
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  // ===== RENDER =====
  return (
    <div className="space-y-3">
      {/* Drop zone - Khu vực kéo thả file */}
      <div
        className={`relative rounded-md border-2 border-dashed p-6 text-center transition-all duration-200 ${
          disabled
            ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-60"
            : isDragOverZone
            ? "border-blue-400 bg-blue-50"
            : "bg-gray-50 border-gray-300 hover:border-gray-400"
        }`}
        onDragEnter={handleDragEnterZone}
        onDragLeave={handleDragLeaveZone}
        onDragOver={handleDragOverZone}
        onDrop={handleDropZone}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={UPLOAD_IMAGE_ACCEPTED_TYPES.join(",")}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        <div className="flex flex-col items-center gap-2">
          <div
            className={`text-3xl ${
              isDragOverZone ? "text-blue-500" : "text-gray-400"
            } transition-colors`}
          >
            📸
          </div>
          <div>
            <p className="text-sm text-gray-600 font-medium">
              Kéo thả ảnh vào đây hoặc{" "}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2 disabled:text-gray-400 disabled:no-underline"
              >
                chọn từ máy tính
              </button>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              JPG, PNG • Tối đa {formatFileSize(maxSizeBytes)}/ảnh • Tối đa{" "}
              {maxImages} ảnh
            </p>
          </div>
        </div>

        {/* Overlay khi đang drag file */}
        {isDragOverZone && (
          <div className="absolute inset-0 rounded-md bg-blue-50/80 flex items-center justify-center pointer-events-none">
            <p className="text-blue-600 font-semibold text-sm">
              ⬇️ Thả ảnh vào đây
            </p>
          </div>
        )}
      </div>

      {/* Error message */}
      {errorMessage && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <span className="text-red-500 text-sm mt-0.5">⚠️</span>
          <p className="text-sm text-red-600">{errorMessage}</p>
          <button
            type="button"
            onClick={() => setErrorMessage("")}
            className="ml-auto text-red-400 hover:text-red-600 text-sm font-bold"
          >
            ✕
          </button>
        </div>
      )}

      {/* Image grid */}
      {images.length > 0 && (
        <div>
          {/* Counter + Status summary */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <p className="text-xs text-gray-500 font-medium">
                {images.length}/{maxImages} ảnh
              </p>
              <SettingTooltip
                label="Cơ chế đăng ảnh tuần tự"
                text="Ảnh sẽ được chọn để đăng lần lượt theo thứ tự từ 1 đến hết. Khi đăng hết danh sách ảnh đã tải lên, hệ thống sẽ tự động quay lại đăng từ ảnh số 1 để bài đăng luôn có hình ảnh đi kèm."
              />
            </div>
            <p className="text-xs text-gray-400">
              💡 Kéo thả để sắp xếp thứ tự
            </p>
          </div>

          {/* Hướng dẫn cơ chế đăng ảnh + Trạng thái - luôn hiển thị khi có ảnh */}
          {images.length > 0 && (() => {
            const hasPostingHistory = isEditMode && uploadImageCursor > 0;
            const currentPos = hasPostingHistory ? uploadImageCursor % images.length : 0;
            const fullCycles = hasPostingHistory ? Math.floor(uploadImageCursor / images.length) : 0;
            const usedInCurrentCycle = hasPostingHistory ? currentPos : 0;
            return (
              <details className="mb-3 border border-blue-100 rounded-lg bg-blue-50/40 overflow-hidden group" open>
                <summary className="flex items-center justify-between px-3 py-2 cursor-pointer select-none hover:bg-blue-50/80 transition-colors list-none [&::-webkit-details-marker]:hidden">
                  <div className="flex items-center gap-2 text-xs font-semibold text-blue-800">
                    <span className="text-sm">📊</span>
                    <span>Trạng thái đăng ảnh tự động</span>
                  </div>
                  <span className="text-[11px] text-blue-600 font-medium group-open:hidden">
                    Chi tiết ▾
                  </span>
                  <span className="text-[11px] text-blue-600 font-medium hidden group-open:inline">
                    Thu gọn ▴
                  </span>
                </summary>
                <div className="px-4 pb-3 pt-2 text-xs text-blue-900 space-y-2 border-t border-blue-100/30 bg-blue-50/20">
                  {/* Tiến trình chi tiết - chỉ hiện khi đã có lịch sử đăng */}
                  {hasPostingHistory && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white shadow-sm">
                          <svg className="w-3.5 h-3.5 stroke-white stroke-[3] fill-none" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span>
                          Đã đăng: <strong className="font-semibold text-blue-900">{usedInCurrentCycle}/{images.length} ảnh</strong> trong lượt hiện tại (Tổng số vòng lặp đã hoàn thành: <strong className="font-semibold text-blue-900">{fullCycles} vòng</strong>).
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white shadow-md animate-pulse">
                          <svg className="w-3 h-3 fill-current ml-0.5" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                        <span>
                          Ảnh đăng tiếp theo: <strong className="font-semibold text-blue-900">#{currentPos + 1}</strong> (tên file: <i className="text-blue-800 font-medium">{images[currentPos]?.filename || "Không tên"}</i>).
                        </span>
                      </div>
                    </>
                  )}

                  {/* Mô tả cơ chế - luôn hiển thị */}
                  <div className="flex items-start gap-2 text-[11px] text-blue-700/80 pt-1 border-t border-blue-100/30">
                    <span className="text-sm leading-none mt-0.5">🔄</span>
                    <p className="font-medium italic leading-relaxed">
                      Ảnh được đăng lần lượt theo thứ tự từ 1 → {images.length}. Khi đăng hết danh sách {images.length} ảnh, hệ thống sẽ tự động quay lại đăng từ ảnh số 1 để bài viết luôn có hình ảnh đi kèm.
                    </p>
                  </div>
                </div>
              </details>
            );
          })()}

          {/* Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((image, index) => {
              const isLocal = image._isLocal;
              const isDragging = draggedIndex === index;
              const isDragOver = dragOverIndex === index;

              return (
                <div
                  key={image.url || index}
                  draggable={!disabled}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnter={(e) => handleDragEnterItem(e, index)}
                  onDragOver={handleDragOverItem}
                  onDrop={(e) => handleDropItem(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`relative rounded-lg overflow-hidden shadow-sm border transition-all duration-200 group ${
                    isDragging
                      ? "opacity-40 scale-95 border-blue-400"
                      : isDragOver
                      ? "border-blue-400 ring-2 ring-blue-200 scale-[1.02]"
                      : "border-gray-200 hover:shadow-md"
                  } ${
                    !disabled ? "cursor-grab active:cursor-grabbing" : ""
                  }`}
                >
                  {/* Image preview */}
                  <div className="aspect-square bg-gray-100 relative">
                    <img
                      src={image.url}
                      alt={image.filename || `Ảnh ${index + 1}`}
                      className="w-full h-full object-cover"
                      draggable={false}
                      loading="lazy"
                    />

                    {/* Drag indicator khi hover */}
                    {!disabled && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <span className="text-white opacity-0 group-hover:opacity-70 transition-opacity text-lg select-none">
                          ⠿
                        </span>
                      </div>
                    )}

                    {/* Drop indicator line */}
                    {isDragOver && draggedIndex !== null && (
                      <div className="absolute inset-0 border-2 border-blue-500 rounded-lg pointer-events-none" />
                    )}
                  </div>

                  {/* Order badge */}
                  <div className="absolute top-1 left-1 bg-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm">
                    {image.order || index + 1}
                  </div>

                  {/* Badge "Chưa lưu" cho ảnh local chưa upload Cloudinary */}
                  {isLocal && (
                    <div
                      className="absolute top-1 left-7 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
                      title="Ảnh chưa được lưu lên hệ thống. Nhấn 'Lưu thiết lập' để upload."
                    >
                      Chưa lưu
                    </div>
                  )}

                  {/* Posting status badge - chỉ hiển thị cho ảnh đã upload (không phải local) */}
                  {!isLocal && (() => {
                    const statusInfo = getImagePostingStatus(index, uploadImageCursor, images.length);
                    if (!statusInfo) return null;

                    const { status, usedCount } = statusInfo;

                    // 1. Trạng thái tiếp theo (next)
                    if (status === "next") {
                      return (
                        <div
                          className="absolute bottom-8 right-2 flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white border-2 border-white shadow-md animate-pulse z-10"
                          title={`Ảnh sẽ đăng tiếp theo${usedCount > 0 ? ` (Đã đăng ${usedCount} lần)` : ""}`}
                        >
                          <svg className="w-4 h-4 fill-current ml-0.5" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      );
                    }

                    // 2. Trạng thái đã đăng (posted)
                    if (status === "posted" || (status === "pending" && usedCount > 0)) {
                      return (
                        <div
                          className={`absolute bottom-8 right-2 flex items-center justify-center bg-emerald-500 text-white border-2 border-white shadow-md z-10 text-[10px] font-bold ${
                            usedCount > 1 ? "px-2 h-8 min-w-[32px] rounded-full" : "w-8 h-8 rounded-full"
                          }`}
                          title={`Đã đăng${usedCount > 1 ? ` (${usedCount} lần)` : ""}`}
                        >
                          <svg className="w-4 h-4 stroke-white stroke-[3.5] fill-none" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          {usedCount > 1 && <span className="ml-0.5">x{usedCount}</span>}
                        </div>
                      );
                    }

                    // 3. Trạng thái chưa đăng / chờ đăng (pending)
                    return (
                      <div
                        className="absolute bottom-8 right-2 flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-500 border-2 border-slate-300 shadow-sm z-10"
                        title="Chưa đăng (Đang chờ lượt)"
                      >
                        <svg className="w-4.5 h-4.5 stroke-slate-500 stroke-[2] fill-none" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="9" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
                        </svg>
                      </div>
                    );
                  })()}

                  {/* Delete button */}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(index);
                      }}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm text-xs"
                      title="Xóa ảnh"
                    >
                      ✕
                    </button>
                  )}

                  {/* Filename */}
                  <div className="px-2 py-1.5 bg-white">
                    <p className="text-xs text-gray-600 truncate" title={image.filename}>
                      {image.filename || "Không tên"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

// Tên hiển thị cho React DevTools
UserImageUploader.displayName = "UserImageUploader";

export default UserImageUploader;
